import React, { useState, useEffect } from 'react';
import * as Plot from "@observablehq/plot";
import PlotFigure from '../PlotFigure';
import DeviceDropdown from '../components/dashboard/DeviceDropdown';
import IntervalButtons from '../components/dashboard/IntervalButtons';
import ErrorMessage from '../components/common/ErrorMessage';
import { useTxRxRetriesData } from '../hooks/useTxRxRetriesData';
import { WifiIcon } from '../components/common/Icons';
import { apiClient } from '../utils/api';
import { useAuth } from '../auth/AuthContext';

export default function TxRxRetriesPage({ 
    selectedDevice, 
    onDeviceChange, 
    selectedInterval, 
    customRange, 
    onIntervalChange, 
    onRangeChange 
}) {
    const [devices, setDevices] = useState([]);
    const { user } = useAuth();
    const userId = user?.id;

    useEffect(() => {
        apiClient.getDevices().then(list => {
            const filtered = userId ? list.filter((device) => device.user_id === userId) : list;
            setDevices(filtered);
            if (filtered.length > 0 && !selectedDevice) {
                onDeviceChange(filtered[0].id);
            }
        }).catch(err => console.error("Failed to load devices", err));
    }, [userId, selectedDevice, onDeviceChange]);

    const { data, loading, error, refresh } = useTxRxRetriesData(
        selectedDevice,
        selectedInterval,
        customRange
    );

    return (
        <div className="dashboard-content dashboard-content--full-width animate-fade-in fade-in">
            <header style={{ marginBottom: '20px' }}>
                <h2><WifiIcon size="28px" /> Tx/Rx Retries</h2>
                <p className="subtitle">Transmission and reception retry metrics.</p>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    <div className="data-controls__device">
                        <div className="data-controls__title">Data analysis controls</div>
                        <DeviceDropdown 
                            devices={devices}
                            selectedDevice={selectedDevice} 
                            onDeviceChange={onDeviceChange} 
                            disabled={loading}
                        />
                    </div>
                    <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Data interval</div>
                        <div className="interval-buttons-with-refresh" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <IntervalButtons
                                selectedInterval={selectedInterval}
                                onIntervalChange={onIntervalChange}
                                customRange={customRange}
                                onCustomRangeChange={onRangeChange}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={refresh}
                                className={`btn-icon ${loading ? 'is-spinning' : ''}`}
                                title="Refresh data"
                            >
                                ↻
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {error && (
                <div className="dashboard-error">
                    <ErrorMessage message={error} />
                </div>
            )}

            {loading && !data && (
                <div className="dashboard-loading">
                    <div className="spinner"></div>
                    <p>Loading Tx/Rx retries data...</p>
                </div>
            )}

            {!loading && !error && data && (
                <TxRxRetriesPlots data={data.records || []} />
            )}
            
            {!loading && !error && data && (!data.records || data.records.length === 0) && (
                <div className="no-data-message">
                    <p>No Tx/Rx retries data found for this interval.</p>
                </div>
            )}
        </div>
    );
}

function TxRxRetriesPlots({ data }) {
    if (!data || data.length === 0) return null;

    const plotData = data.map(d => ({
        ...d,
        t: new Date(d.ts),
        tx_all: parseFloat(d.tx_all || 0),
        retrans: parseFloat(d.retrans || 0),
        hw_rx_fcs: parseFloat(d.hw_rx_fcs || 0),
        rx_fcs_pct: parseFloat(d.rx_fcs_pct || 0),
    }));

    const txAllPlotOptions = {
        y: { grid: true, label: "Total Transmissions" },
        x: { type: 'time', grid: true },
        marks: [
            Plot.ruleY([0]),
            Plot.line(plotData, { x: 't', y: 'tx_all', stroke: 'blue' }),
            Plot.tip(plotData, Plot.pointerX({
                x: 't', y: 'tx_all',
                title: (d) => `Total Transmissions: ${d.tx_all}\nTime: ${d.t.toLocaleString()}`,
                pointer: 'mouse', className: 'plot-tip neon-plot-tip', textPadding: 12, pointerSize: 14, lineHeight: 1.6, fontSize: 12, fontWeight: '600',
            })),
        ],
    };

    const retransPlotOptions = {
        y: { grid: true, label: "Total Retransmissions" },
        x: { type: 'time', grid: true },
        marks: [
            Plot.ruleY([0]),
            Plot.line(plotData, { x: 't', y: 'retrans', stroke: 'red' }),
            Plot.tip(plotData, Plot.pointerX({
                x: 't', y: 'retrans',
                title: (d) => `Retransmissions: ${d.retrans}\nTime: ${d.t.toLocaleString()}`,
                pointer: 'mouse', className: 'plot-tip neon-plot-tip', textPadding: 12, pointerSize: 14, lineHeight: 1.6, fontSize: 12, fontWeight: '600',
            })),
        ],
    };

    const hwRxFcsPlotOptions = {
        y: { grid: true, label: "rx_fcs (Errors)" },
        x: { type: 'time', grid: true },
        marks: [
            Plot.ruleY([0]),
            Plot.line(plotData, { x: 't', y: 'hw_rx_fcs', stroke: 'green' }),
            Plot.tip(plotData, Plot.pointerX({
                x: 't', y: 'hw_rx_fcs',
                title: (d) => `Hardware rx_fcs: ${d.hw_rx_fcs}\nTime: ${d.t.toLocaleString()}`,
                pointer: 'mouse', className: 'plot-tip neon-plot-tip', textPadding: 12, pointerSize: 14, lineHeight: 1.6, fontSize: 12, fontWeight: '600',
            })),
        ],
    };

    const rxFcsPctPlotOptions = {
        y: { grid: true, label: "% (rx_fcs / Total RX)" },
        x: { type: 'time', grid: true },
        marks: [
            Plot.ruleY([0]),
            Plot.line(plotData, { x: 't', y: 'rx_fcs_pct', stroke: 'purple' }),
            Plot.tip(plotData, Plot.pointerX({
                x: 't', y: 'rx_fcs_pct',
                title: (d) => `rx_fcs %: ${d.rx_fcs_pct.toFixed(2)}%\nTime: ${d.t.toLocaleString()}`,
                pointer: 'mouse', className: 'plot-tip neon-plot-tip', textPadding: 12, pointerSize: 14, lineHeight: 1.6, fontSize: 12, fontWeight: '600',
            })),
        ],
    };

    return (
        <div className="plots-container">
            <div className="plot-row">
                <div className="plot-column">
                    <div className="plot-section">
                        <header className="plot-section__header plot-section__header--compact">
                            <h3><WifiIcon size="24px" /> Total LMAC TX (tx_all) Over Time</h3>
                        </header>
                        <PlotFigure options={txAllPlotOptions} />
                    </div>
                </div>
            </div>
            <div className="plot-row">
                <div className="plot-column">
                    <div className="plot-section">
                        <header className="plot-section__header plot-section__header--compact">
                            <h3><WifiIcon size="24px" /> Total Retransmissions (lrc + src)</h3>
                        </header>
                        <PlotFigure options={retransPlotOptions} />
                    </div>
                </div>
            </div>
            <div className="plot-row">
                <div className="plot-column">
                    <div className="plot-section">
                        <header className="plot-section__header plot-section__header--compact">
                            <h3><WifiIcon size="24px" /> Hardware rx_fcs Over Time</h3>
                        </header>
                        <PlotFigure options={hwRxFcsPlotOptions} />
                    </div>
                </div>
            </div>
            <div className="plot-row">
                <div className="plot-column">
                    <div className="plot-section">
                        <header className="plot-section__header plot-section__header--compact">
                            <h3><WifiIcon size="24px" /> Percentage of rx_fcs to Total RX Packets</h3>
                        </header>
                        <PlotFigure options={rxFcsPctPlotOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
}
